/**
 * STORY — encounter story cards sealed at the daily close (ADR-0023, V2-F8a).
 *
 * Every resolved encounter produces one ``story.card.issued`` event and one
 * ``encounter.resolved`` event (V2-L7c). The view owns no data and invents no
 * numbers: it reads the player's events when the server exposes them and the
 * authoritative day-close payload (``GET /core/day/close``) otherwise, then
 * renders the card title/body or the encounter facts exactly as stored.
 *
 * Degrades cleanly: offline, v2 off, no ticket, an unsealed day and a day with
 * no pair all render an honest empty state instead of a guessed card.
 */
import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, { coreErrorMessage, formatMinor } from '../../stores/coreSession';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';

export const STORY_CARD_EVENT = 'story.card.issued';
export const ENCOUNTER_RESOLVED_EVENT = 'encounter.resolved';

const state = {
  cards: [],
  loaded: false,
  loading: false,
  error: null,
  v2Off: false,
  ticketMissing: false,
  pendingClose: false,
  source: null,
  selected: 0,
};

let loadPromise = null;

export function _resetStoryState(overrides = {}) {
  Object.assign(
    state,
    {
      cards: [],
      loaded: false,
      loading: false,
      error: null,
      v2Off: false,
      ticketMissing: false,
      pendingClose: false,
      source: null,
      selected: 0,
    },
    overrides,
  );
  loadPromise = null;
}

export function storyErrorMessage(error) {
  if (!error) return 'STORY LINE DOWN';
  if (error.code === 'CORE-404-012') return 'STORY LINE CLOSED // V2 OFFLINE';
  if (error.code === 'CORE-404-013') return 'THE DAY IS NOT SEALED YET.';
  return coreErrorMessage(error);
}

function numberOrNull(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function newestFirst(left, right) {
  const dayDelta = (right.dayIndex ?? 0) - (left.dayIndex ?? 0);
  if (dayDelta !== 0) return dayDelta;
  const leftAt = String(left.createdAt || '');
  const rightAt = String(right.createdAt || '');
  if (leftAt !== rightAt) return leftAt < rightAt ? 1 : -1;
  return String(left.cardId || '').localeCompare(String(right.cardId || ''));
}

/**
 * Build the cards from the L7c events when present, otherwise from the close's
 * authoritative ``encounters`` list. Only the player's own collisions become
 * cards: the close is world-scoped and may carry other residents.
 */
export function mapStoryCards({ events = [], dayClose = null, playerId = null } = {}) {
  const list = Array.isArray(events) ? events : [];
  const cardEvents = list.filter(
    (event) => event?.type === STORY_CARD_EVENT && event.payload,
  );

  if (cardEvents.length) {
    const resolved = new Map(
      list
        .filter((event) => event?.type === ENCOUNTER_RESOLVED_EVENT && event.aggregateId)
        .map((event) => [event.aggregateId, event.payload || {}]),
    );
    return cardEvents
      .map((event) => {
        const encounterId =
          String(event.aggregateId || '').replace(/^sc_/, '') || null;
        const encounter = (encounterId && resolved.get(encounterId)) || {};
        return {
          cardId: event.aggregateId || encounterId,
          encounterId,
          title: event.payload.title || null,
          body: event.payload.body || null,
          kind: event.payload.kind || 'encounter',
          seasonId: event.payload.seasonId || encounter.seasonId || null,
          dayIndex: numberOrNull(event.payload.dayIndex ?? encounter.dayIndex),
          createdAt: event.payload.createdAt || event.occurredAt || null,
          districtId: encounter.districtId || null,
          riskTier: null,
          aPlayerId: encounter.aPlayerId || null,
          bPlayerId: encounter.bPlayerId || null,
          winnerId: encounter.winnerId || null,
          loserId: encounter.loserId || null,
          tollPaid: encounter.tollPaid || null,
          source: 'event',
        };
      })
      .sort(newestFirst);
  }

  const encounters = Array.isArray(dayClose?.encounters) ? dayClose.encounters : [];
  return encounters
    .filter(
      (entry) =>
        playerId &&
        (entry?.aPlayerId === playerId || entry?.bPlayerId === playerId),
    )
    .map((entry) => ({
      cardId: entry.encounterId || null,
      encounterId: entry.encounterId || null,
      title: null,
      body: null,
      kind: 'encounter',
      seasonId: dayClose?.seasonId || null,
      dayIndex: numberOrNull(dayClose?.dayIndex),
      createdAt: dayClose?.sealedAt || null,
      districtId: entry.districtId || null,
      riskTier: entry.riskTier || null,
      aPlayerId: entry.aPlayerId || null,
      bPlayerId: entry.bPlayerId || null,
      winnerId: entry.winnerId || null,
      loserId: entry.loserId || null,
      tollPaid: entry.tollPaid || null,
      source: 'close',
    }))
    .sort(newestFirst);
}

export function districtLabel(districtId) {
  const value = String(districtId || '').replace(/_/g, ' ').trim();
  return value ? value.toUpperCase() : 'DISTRICT UNKNOWN';
}

export function storyCardTitle(card) {
  if (card?.title) return String(card.title);
  return `ENCOUNTER // ${districtLabel(card?.districtId)}`;
}

export function shortId(value) {
  const id = String(value || '');
  return id ? id.slice(-6).toUpperCase() : '--';
}

export function storyOutcome(card, playerId) {
  if (!playerId || !card?.winnerId) return null;
  if (card.winnerId === playerId) return 'TOLL COLLECTED';
  if (card.loserId === playerId) return 'TOLL PAID';
  return null;
}

export function wrapText(value, maxChars = 40) {
  const words = String(value || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  words.forEach((word) => {
    if (!line) {
      line = word;
    } else if (`${line} ${word}`.length <= maxChars) {
      line = `${line} ${word}`;
    } else {
      lines.push(line);
      line = word;
    }
  });
  if (line) lines.push(line);
  return lines;
}

export function storyCardFacts(card, playerId) {
  const outcome = storyOutcome(card, playerId);
  const sides = card?.winnerId
    ? [
        ['WINNER', shortId(card.winnerId)],
        ['LOSER', shortId(card.loserId)],
      ]
    : [
        ['PLAYER A', shortId(card?.aPlayerId)],
        ['PLAYER B', shortId(card?.bPlayerId)],
      ];
  return [
    ['DAY', card?.dayIndex == null ? '--' : String(card.dayIndex)],
    ['DISTRICT', districtLabel(card?.districtId)],
    ['RISK', String(card?.riskTier || '--').toUpperCase()],
    ['OUTCOME', outcome || '--'],
    ['TOLL', card?.tollPaid ? formatMinor(card.tollPaid) : '--'],
    ...sides,
  ];
}

export async function loadStoryCards() {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    state.loading = true;
    state.error = null;
    state.v2Off = false;
    state.ticketMissing = false;
    state.pendingClose = false;
    try {
      const session = useCoreSession.getState();
      const playerId = session.playerId || session.state?.playerId || null;
      const events = await session.loadEvents(200).catch(() => []);
      const hasStoryEvents = events.some(
        (event) => event?.type === STORY_CARD_EVENT,
      );

      let dayClose = null;
      if (!hasStoryEvents) {
        let ticket = session.state?.ticket ?? session.ticket ?? null;
        if (ticket == null) {
          try {
            ticket = await session.loadTicket();
          } catch (error) {
            if (error?.code === 'CORE-404-012') {
              state.v2Off = true;
              state.loaded = true;
              return;
            }
            throw error;
          }
        }

        if (!ticket?.seasonId || !ticket?.dayIndex) {
          state.ticketMissing = true;
        } else {
          const days = [
            Number(ticket.dayIndex),
            Number(ticket.dayIndex) - 1,
          ].filter((day) => Number.isInteger(day) && day >= 1);
          for (const day of days) {
            try {
              const payload = await session.loadDayClose(ticket.seasonId, day);
              dayClose = payload?.dayClose || null;
              if (dayClose) {
                state.pendingClose = false;
                break;
              }
            } catch (error) {
              if (error?.code === 'CORE-404-013') {
                state.pendingClose = true;
                continue;
              }
              if (error?.code === 'CORE-404-012') {
                state.v2Off = true;
                state.loaded = true;
                return;
              }
              throw error;
            }
          }
        }
      }

      const cards = mapStoryCards({ events, dayClose, playerId });
      state.cards = cards;
      state.source = hasStoryEvents ? 'events' : dayClose ? 'close' : null;
      state.loaded = true;
      if (state.selected >= cards.length) state.selected = 0;
    } catch (error) {
      state.error = storyErrorMessage(error);
      state.loaded = true;
    } finally {
      state.loading = false;
      loadPromise = null;
    }
  })();
  return loadPromise;
}

function text(ctx, value, x, y, color = TERMINAL.TEXT, scale = 1) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, scale);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

function drawTop(ctx, safe, session) {
  const mobile = safe.width < 500;
  const online = session.status === 'online';
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, 24);
  rule(ctx, 0, 23, safe.width);
  text(ctx, 'DOPEOS // STORY', 8, 4);
  if (!mobile) text(ctx, 'ENCOUNTER CARDS // SEALED AT CLOSE', 216, 4, TERMINAL.TEXT_DIM);
  text(
    ctx,
    online ? 'LIVE' : 'OFFLINE',
    safe.width - 56,
    4,
    online ? TERMINAL.GREEN : TERMINAL.TEXT_DIM,
  );
}

function emptyCopy() {
  if (state.v2Off) return ['STORY LINE CLOSED // V2 OFFLINE', 'THE STORY LINE OPENS WITH V2.'];
  if (state.ticketMissing) {
    return ['NO TICKET ON FILE.', 'BUY A TICKET IN TICKETS TO ENTER A DISTRICT.'];
  }
  if (state.pendingClose) return ['THE DAY IS NOT SEALED YET.', 'CARDS ARRIVE AFTER THE CLOSE.'];
  if (state.loaded) return ['NO ENCOUNTERS ON FILE.', 'THE CLOSE PAIRS WHO WOKE TOGETHER.'];
  return ['READING THE CARDS...', 'WAITING ON THE LINE.'];
}

function drawList(ctx, x, y, w, h, cards, mobile = false) {
  DopeUI.Panel(ctx, x, y, w, h, 'STORY CARDS');
  text(ctx, 'DAY  CARD', x + 10, y + 24, TERMINAL.TEXT_DIM);
  text(
    ctx,
    `${String(cards.length).padStart(2, '0')} ON FILE`,
    x + w - 88,
    y + 24,
    TERMINAL.TEXT_DIM,
  );
  rule(ctx, x + 8, y + 40, w - 16);

  const rowH = mobile ? 48 : 40;
  const top = y + 52;
  const maxRows = Math.max(1, Math.floor((h - 58) / rowH));
  const visible = cards.slice(0, maxRows);
  const hidden = cards.length - visible.length;

  visible.forEach((card, index) => {
    const rowY = top + index * rowH;
    const selected = index === state.selected;
    if (selected) {
      ctx.fillStyle = TERMINAL.GREEN_DARK;
      ctx.fillRect(x + 6, rowY - 6, w - 12, rowH - 4);
    }
    const day = card.dayIndex == null ? '--' : String(card.dayIndex).padStart(2, '0');
    text(
      ctx,
      `${selected ? '>' : ' '}${day}`,
      x + 10,
      rowY,
      selected ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT,
    );
    text(
      ctx,
      storyCardTitle(card).slice(0, mobile ? 28 : 24),
      x + 40,
      rowY,
      selected ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT,
    );
    const outcome = storyOutcome(card, useCoreSession.getState().playerId);
    if (outcome) text(ctx, outcome, x + 40, rowY + 16, TERMINAL.TEXT_DIM);
    interactiveRegistry.register({
      id: `story-card-${card.cardId || index}`,
      x: x + 6,
      y: rowY - 6,
      w: w - 12,
      h: rowH - 4,
      onClick: () => {
        state.selected = index;
      },
    });
  });

  if (!cards.length) {
    const [headline, detail] = state.error
      ? [state.error, 'RETRY WHEN THE LINE RETURNS.']
      : emptyCopy();
    const maxChars = Math.max(12, Math.floor((w - 24) / 8));
    let cursor = top;
    wrapText(headline, maxChars).forEach((line) => {
      text(ctx, line, x + 12, cursor, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
      cursor += 16;
    });
    wrapText(detail, maxChars).forEach((line) => {
      text(ctx, line, x + 12, cursor, TERMINAL.TEXT_DIM);
      cursor += 16;
    });
  } else if (hidden > 0) {
    text(ctx, `+${hidden} MORE ON FILE`, x + 12, y + h - 18, TERMINAL.TEXT_DIM);
  }
}

function drawDetail(ctx, x, y, w, h, card, playerId, mobile = false) {
  DopeUI.Panel(ctx, x, y, w, h, 'CARD');
  if (!card) {
    text(ctx, 'NO CARD SELECTED.', x + 12, y + 30, TERMINAL.TEXT_DIM);
    return;
  }

  text(ctx, storyCardTitle(card).slice(0, mobile ? 40 : 42), x + 12, y + 28, TERMINAL.TEXT_BRIGHT);
  let cursor = y + 48;
  if (card.body) {
    wrapText(card.body, mobile ? 42 : 42)
      .slice(0, mobile ? 3 : 4)
      .forEach((line) => {
        text(ctx, line, x + 12, cursor, TERMINAL.TEXT);
        cursor += 16;
      });
    cursor += 6;
  }

  storyCardFacts(card, playerId).forEach(([label, value]) => {
    if (cursor + 14 > y + h - 24) return;
    text(ctx, label, x + 12, cursor, TERMINAL.TEXT_DIM);
    text(ctx, value, x + 84, cursor, TERMINAL.TEXT);
    cursor += 18;
  });

  const source = card.source === 'event' ? 'CLOSE EVENT // SEALED RECORD' : 'CLOSE PAYLOAD // SEALED RECORD';
  text(ctx, source, x + 12, y + h - 18, TERMINAL.TEXT_DIM);
}

function drawFooter(ctx, safe, session, mobile) {
  const online = session.status === 'online';
  const canRefresh = online && !state.loading;
  const note = state.error;

  if (mobile) {
    const buttonY = 530;
    const backY = 584;
    if (note) {
      text(ctx, `// ${note}`.slice(0, 38), 8, buttonY - 16, TERMINAL.TEXT_BRIGHT);
    }
    DopeUI.Button(
      ctx,
      8,
      buttonY,
      safe.width - 16,
      44,
      state.loading ? 'READING...' : 'REFRESH',
      canRefresh ? 'hover' : 'disabled',
    );
    if (canRefresh) {
      interactiveRegistry.register({
        id: 'story-refresh',
        x: 8,
        y: buttonY,
        w: safe.width - 16,
        h: 44,
        onClick: () => loadStoryCards(),
      });
    }
    DopeUI.Button(ctx, 8, backY, safe.width - 16, 44, 'BACK TO DESKTOP', 'normal');
    interactiveRegistry.register({
      id: 'story-back',
      x: 8,
      y: backY,
      w: safe.width - 16,
      h: 44,
      onClick: () => navigateTo('main_menu'),
    });
    return;
  }

  rule(ctx, 0, 320, safe.width);
  text(ctx, 'SEALED RECORD // CARDS ARE ISSUED AT THE CLOSE', 8, 330, TERMINAL.TEXT_DIM);
  DopeUI.Button(
    ctx,
    8,
    344,
    304,
    28,
    state.loading ? 'READING...' : 'REFRESH',
    canRefresh ? 'hover' : 'disabled',
  );
  if (canRefresh) {
    interactiveRegistry.register({
      id: 'story-refresh',
      x: 8,
      y: 344,
      w: 304,
      h: 28,
      onClick: () => loadStoryCards(),
    });
  }
  DopeUI.Button(ctx, 320, 344, safe.width - 328, 28, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'story-back',
    x: 320,
    y: 344,
    w: safe.width - 328,
    h: 28,
    onClick: () => navigateTo('main_menu'),
  });
  if (note) text(ctx, `// ${note}`.slice(0, 56), 8, 384, TERMINAL.TEXT_BRIGHT);
}

registerView('story', function renderStory(ctx, time, width, height) {
  interactiveRegistry.clear();
  const session = useCoreSession.getState();
  if (session.status === 'online' && session.token && !state.loaded && !loadPromise) {
    loadStoryCards();
  }

  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);

  drawTop(ctx, safe, session);

  const mobile = safe.width < 500;
  const playerId = session.playerId || session.state?.playerId || null;

  if (session.status !== 'online') {
    DopeUI.Panel(ctx, 8, 36, safe.width - 16, 120, 'NO LINE');
    text(ctx, 'OFFLINE // NO LINK.', 20, 70, TERMINAL.TEXT_DIM);
    text(ctx, 'THE SEALED CARDS WAIT.', 20, 92, TERMINAL.TEXT_DIM);
  } else if (mobile) {
    drawList(ctx, 8, 32, safe.width - 16, 250, state.cards, true);
    drawDetail(ctx, 8, 290, safe.width - 16, 230, state.cards[state.selected], playerId, true);
  } else {
    drawList(ctx, 8, 36, 248, 280, state.cards);
    drawDetail(ctx, 264, 36, 368, 280, state.cards[state.selected], playerId);
  }

  drawFooter(ctx, safe, session, mobile);
});

export default 'story';
